import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Col, Row, Form, Button, Card, Badge, Spinner, Offcanvas, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useAppContext } from 'providers/AppProvider';
import { useSelector } from 'react-redux';
import { RootState } from 'store';




type Agent = {
  uuid?: string | null;
  name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

type Customer = {
  name?: string | null;
  email?: string | null;
  country?: string | null; // "RO"
  region?: string | null;  // "București"
  device?: string | null;  // "Computer"
  os?: string | null;      // "Mac OS 10.15"
  browser?: string | null; // "Chrome"
};

type Session = {
  id: number;
  session_uuid: string;
  opened_at: string; // ISO
  closed_at?: string | null;
  last_activity?: string | null;
  subject?: string | null;
  tags?: any;
  customer: Customer;
  agent?: Agent;
};


const PAGE_SIZE = 30;

const ChatArchivesPage = () => {


  const token = useSelector((state: RootState) => state.auth.token);

  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  const [items, setItems] = useState<Session[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const observerRef = useRef<HTMLDivElement | null>(null);
  const cancelSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  const {
    config: { isDark },
  } = useAppContext();

  // (Optional) you had this in your starter file; keep if useful for showing totals, etc.
  const trafficInformation = useSelector((state: RootState) => state.traffic.customers);

  const filtersMemo = useMemo(
    () => ({
      name: nameFilter.trim(),
      email: emailFilter.trim(),
      from: dateFrom ? new Date(dateFrom).toISOString() : '',
      to: dateTo ? new Date(dateTo).toISOString() : '',
    }),
    [nameFilter, emailFilter, dateFrom, dateTo]
  );

  const resetAndFetch = () => {
    setItems([]);
    setPage(0);
    setHasMore(true);
  };


  const getInitial = (name?: string | null) => {
  const t = (name || '').trim();
  return t ? t[0].toUpperCase() : 'U';
};




  const countryCodeToFlag = (code?: string | null) => {
  if (!code || code.length !== 2) return "❓";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map(c => 127397 + c.charCodeAt(0)));
};

// Get localized country name from ISO 3166-1 alpha-2
const regionNames = new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' });
const countryCodeToName = (code?: string | null) => {
  if (!code) return null;
  try {
    return regionNames.of(code.toUpperCase()) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
};

// Ensure absolute avatar URL
const withApiBase = (url?: string | null) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://api20.easychatdesk.com${url.startsWith('/') ? '' : '/'}${url}`;
};

const fetchPage = async (pageIndex: number) => {
  if (!hasMore && pageIndex !== 0) return;
  if (pageIndex === 0) setInitialLoading(true);
  else setLoadingMore(true);

  cancelSourceRef.current?.cancel('New request started');
  cancelSourceRef.current = axios.CancelToken.source();

  try {
    const { data } = await axios.get('https://api20.easychatdesk.com/api/chat-archives', {
      params: {
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
        name: filtersMemo.name || undefined,
        email: filtersMemo.email || undefined,
        from: filtersMemo.from || undefined,
        to: filtersMemo.to || undefined,
      },
      headers: {
        Authorization: `Bearer ${token}`, // 👈 send JWT here
      },
      cancelToken: cancelSourceRef.current.token,
    });

    const pageItems: Session[] = Array.isArray(data?.sessions) ? data.sessions : [];
    setItems(prev => (pageIndex === 0 ? pageItems : [...prev, ...pageItems]));
    setHasMore(pageItems.length === PAGE_SIZE);
    setPage(pageIndex);
  } catch (err) {
    if (!axios.isCancel(err)) {
      console.error('Failed to fetch chat archives:', err);
    }
  } finally {
    setInitialLoading(false);
    setLoadingMore(false);
  }
};


  // Load first page and when filters change
  useEffect(() => {
    resetAndFetch();
  }, [filtersMemo.name, filtersMemo.email, filtersMemo.from, filtersMemo.to]);

  useEffect(() => {
    // after reset, actually fetch the first page
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore === true && page === 0 && items.length === 0]); // simple trigger after reset

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingMore && hasMore && !initialLoading) {
          fetchPage(page + 1);
        }
      },
      { rootMargin: '200px 0px 200px 0px' }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [page, hasMore, loadingMore, initialLoading]);

  const handleRowClick = (session: Session) => {
    setSelectedSession(session);
    setIsSidePanelOpen(true);
  };
  const closeSidePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedSession(null);
  };

  const clearFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      // You can localize with Intl.DateTimeFormat if you prefer
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  const cardBg = isDark ? 'bg-dark border-secondary' : 'bg-white';

  return (
    <>
      <Row className="gy-3 mb-4 justify-content-between">
        <Col md={12} xs="auto">
          <h2 className="mb-1 text-body-emphasis">Chat Archives</h2>
          <h5 className="text-body-tertiary fw-semibold">
            Check the archives and replies, answer and write to each customer.
          </h5>
        </Col>
      </Row>

      {/* Filters */}
      <Card className={`mb-4 ${cardBg}`}>
        <Card.Body>
          <Form>
            <Row className="g-3 align-items-end">
              <Col md={3}>
                <Form.Label className="fw-semibold">Customer name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. John Doe"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="fw-semibold">Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="e.g. john@site.com"
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="fw-semibold">From</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Form.Label className="fw-semibold">To</Form.Label>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
              <Col md={2} className="d-flex gap-2">
                <Button
                  variant="primary"
                  className="flex-grow-1"
                  onClick={() => resetAndFetch()}
                >
                  Apply
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={clearFilters}
                  title="Reset filters"
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Meta / Optional */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="small text-body-tertiary">
          Showing <strong>{items.length}</strong> result{items.length !== 1 ? 's' : ''}{' '}
          {hasMore && '… (more as you scroll)'}
        </div>
        {Array.isArray(trafficInformation) && (
          <div className="small text-body-tertiary">
            Live visitors in memory: <strong>{trafficInformation.length}</strong>
          </div>
        )}
      </div>

      {/* Sessions list */}
      <Row className="g-3">
        {initialLoading && items.length === 0 ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-5 text-body-tertiary">No archived chats found.</div>
        ) : (
          
        
          items.map((s) => {
  const cust = s.customer || {};
  const ag   = s.agent || {};

  const displayName   = (cust.name || '').trim() || 'Unnamed';
  const displayEmail  = (cust.email || '').trim() || 'missing';
  const countryCode   = cust.country || null;
  const region        = cust.region || null;
  const flag          = countryCodeToFlag(countryCode);
  const countryName   = countryCodeToName(countryCode) || '—';
  const countryLabel  = region ? `${countryName}, ${region}` : countryName;

  const deviceLabel   = cust.device || '—';
  const uaTooltip     = [cust.browser, cust.os].filter(Boolean).join(' • ') || 'Device';

  const agentName     = ag.name || 'Agent';
  const agentAvatar   = withApiBase(ag.avatar_url);

  const initial       = getInitial(cust.name);

  return (
    <Col md={12} key={s.id}>
      <Card
        className={`h-100 hover-shadow-sm ${cardBg} border`}
        role="button"
        onClick={() => handleRowClick(s)}
      >
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-start">
            {/* Left: avatar + customer + time */}
            <div className="me-3 d-flex align-items-start gap-3">
              {/* Initial avatar */}
              <div
                className="flex-shrink-0"
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 16,
                  lineHeight: 1,
                  background: isDark ? '#6c757d' : '#0d6efd', // dark: secondary, light: primary
                  color: '#fff',
                }}
                title={displayName}
                aria-label={`Customer ${displayName} avatar`}
              >
                {initial}
              </div>

              {/* Name + email + time */}
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="fw-bold">{displayName}</span>
                  <span className="text-body-tertiary">•</span>
                  <span className="text-muted">{displayEmail}</span>
                </div>
                <div className="small text-body-tertiary mt-1">
                  Started: {formatDate(s.opened_at)}
                </div>
              </div>
            </div>

            {/* Right: location/device + agent */}
            <div className="d-flex align-items-center gap-4 mt-2 mt-md-0">
              {/* Country + region */}
              <div className="d-flex align-items-center gap-2">
                <Badge bg="secondary" className="text-uppercase d-flex align-items-center gap-2">
                  <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>{flag}</span>
                  <span className="text-white">{countryLabel}</span>
                </Badge>
              </div>

              {/* Device (tooltip shows UA) */}
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tt-${s.id}`}>{uaTooltip}</Tooltip>}
              >
                <Badge bg="light" text="dark" className="border">
                  {deviceLabel}
                </Badge>
              </OverlayTrigger>

              {/* Agent avatar + name */}
              <div className="d-flex align-items-center gap-2">
                {agentAvatar ? (
                  <img
                    src={agentAvatar}
                    alt={agentName}
                    width={28}
                    height={28}
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#e9ecef',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                    }}
                    title={agentName}
                  >
                    👤
                  </div>
                )}
                <span className="small text-body-secondary">{agentName}</span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
})

        )}
      </Row>

      {/* Infinite scroll sentinel */}
      <div ref={observerRef} className="py-4 d-flex justify-content-center">
        {loadingMore && hasMore && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span className="small text-body-tertiary">Loading more…</span>
          </div>
        )}
      </div>

      {/* Side panel (details) */}
      <Offcanvas
        show={isSidePanelOpen}
        onHide={closeSidePanel}
        placement="end"
        backdrop
        scroll
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Session details</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {!selectedSession ? (
            <div className="text-body-tertiary">No session selected.</div>
          ) : (
            <>
              <div className="mb-3">
                <div className="fw-bold">{selectedSession.customer.name || 'Unnamed'}</div>
                <div className="text-muted">{selectedSession.customer.email || 'missing'}</div>
              </div>

              <div className="mb-2 small">
                <div>
                  <span className="text-body-tertiary">Started:</span>{' '}
                  {formatDate(selectedSession.opened_at)}
                </div>
                <div>
                  <span className="text-body-tertiary">Country:</span>{' '}
                  {countryCodeToName(selectedSession.customer?.country) || '—'}
  {selectedSession.customer?.region ? `, ${selectedSession.customer.region}` : ''}
                </div>
                <div>
                  <span className="text-body-tertiary">Device:</span>{' '}
                  {selectedSession.customer?.device || '—'}
                </div>
               {[selectedSession.customer?.browser, selectedSession.customer?.os].filter(Boolean).join(' • ')}
 
              </div>

              {/* TODO: You can render the thread preview/messages here */}
              <hr />
              <div className="small text-body-tertiary">
                (Thread preview/messages could go here.)
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default ChatArchivesPage;
